<template>
  <div class="content">
    <div class="md-layout">
      <h3 class="md-headline">
        Create New Node
      </h3>
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <form
          class="node-form"
          @submit.prevent="createNode"
        >
          <div class="node-form__left">
            <h3 class="h3">
              Settings
            </h3>
            <div class="node-form__general">
              <div class="node-form__general--left">
                <md-field>
                  <label>Protocol</label>
                  <md-select
                    id="protocol"
                    v-model="node.protocol"
                    class="md-select-menu"
                    name="protocol"
                  >
                    <md-option
                      class="md-select-value"
                      value="OpenVPN_UDP"
                    >
                      OpenVPN_UDP
                    </md-option>
                    <md-option
                      class="md-select-value"
                      value="OpenVPN_TCP"
                    >
                      OpenVPN_TCP
                    </md-option>
                    <md-option
                      class="md-select-value"
                      value="IKEv1"
                    >
                      IKEv1
                    </md-option>
                    <md-option
                      class="md-select-value"
                      value="IKEv2"
                    >
                      IKEv2
                    </md-option>
                    <md-option
                      class="md-select-value"
                      value="L2TPOverIPSEC"
                    >
                      L2TPOverIPSEC
                    </md-option>
                    <md-option
                      class="md-select-value"
                      value="GRPC"
                    >
                      GRPC
                    </md-option>
                    <md-option
                      class="md-select-value"
                      value="LocalDNS"
                    >
                      LocalDNS
                    </md-option>
                    <md-option
                      class="md-select-value"
                      value="CustomDNSConfigurations"
                    >
                      CustomDNSConfigurations
                    </md-option>
                    <md-option
                      class="md-select-value"
                      value="Hysteria2"
                    >
                      Hysteria2
                    </md-option>
                  </md-select>
                  <div
                    v-if="submitted && !$v.node.protocol.required"
                    class="error-feedback"
                  >
                    Protocol
                    is required
                  </div>
                </md-field>
              </div>
              <div class="node-form__general--right">
                <md-field>
                  <label>Provider</label>
                  <div
                    v-if="submitted && !$v.node.provider.required"
                    class="error-feedback"
                  >
                    Field is
                    required
                  </div>
                  <md-select
                    id="provider"
                    v-model="node.provider"
                    name="protocol"
                  >
                    <md-option value="ProVPN">
                      ProVPN
                    </md-option>
                    <md-option value="Nord">
                      Nord
                    </md-option>
                  </md-select>
                </md-field>
              </div>
            </div>

            <grpc-node-form
              v-if="node.protocol === 'GRPC'"
              :node.sync="node"
            />

            <local-dns-node-form
              v-else-if="node.protocol.includes('DNS') || node.protocol === 'Hysteria2'"
              :node.sync="node"
            />

            <open-vpn-node-form
              v-else
              :node.sync="node"
            />

            <metadata-form :metadata="node.metadata" />

            <h3 class="h3">
              Server Bundles
            </h3>
            <div class="node-form__tags tags">
              <div class="tags_selected">
                <md-chip
                  v-for="sb in sbs"
                  :key="sb"
                  class="md-primary"
                  md-deletable
                  @click="deleteChip(sb)"
                >
                  {{ sb }}
                </md-chip>
              </div>
              <div class="tags__input-field">
                <md-autocomplete
                  v-model="sbValue"
                  class="tags__input"
                  :md-options="sbIndex"
                  :md-fuzzy-search="false"
                >
                  <label>Enter name</label>

                  <template
                    slot="md-autocomplete-item"
                    slot-scope="{ item, term }"
                  >
                    <md-highlight-text :md-term="term">
                      {{ item }}
                    </md-highlight-text>
                  </template>
                </md-autocomplete>
                <md-button
                  class="md-primary md-round tag__add-btn"
                  @click="addServer()"
                >
                  Add
                </md-button>
              </div>
            </div>

            <h3 class="h3">
              Location
            </h3>
            <div class="location">
              <md-field>
                <label>Longitude</label>
                <md-input v-model="node.location.longitude" />
              </md-field>
              <md-field>
                <label>Latitude</label>
                <md-input v-model="node.location.latitude" />
              </md-field>
            </div>
            <div class="location">
              <div>
                <country-autocomplete
                  label="Country"
                  @countrySelected="updateLocationCountryCode"
                />
                <div
                  v-if="$v.node.location.countryCode.$error"
                  class="error-feedback"
                >
                  Invalid country
                </div>
              </div>
            </div>
            <div class="location">
              <city-node-form
                v-model="node.location.city"
                :error="$v.node.location.city.$error"
              />
            </div>

            <md-button
              type="submit"
              class="md-success form-submit"
              :disabled="processing"
            >
              Save
            </md-button>
            <div class="error-feedback">
              {{ errorMsg }}
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script>
    import {required} from "vuelidate/lib/validators";
    import {isValidCountryCode} from "@/assets/countries_iso/all-iso-codes";
    import {countryCodeToName, countryCodeToUkName} from "../assets/countries_iso/all-iso-codes";

    import OpenVPNNodeForm from "@/components/Forms/nodes/OpenVPNNodeForm";
    import GRPCNodeForm from "@/components/Forms/nodes/GRPCNodeForm";
    import LocalDNSNodeForm from "@/components/Forms/nodes/LocalDNSNodeForm";
    import MetadataForm from "@/components/Forms/nodes/MetadataForm";
    import CountryAutocomplete from "@/components/Forms/Fields/CountryAutocomplete";
    import CityNodeForm from "@/components/Forms/nodes/CityNodeForm";

    import filterNodeBody from "@/utils/nodeBodyFilter";
    import { LOCALES_LIST } from "@/utils/currentLocalesList";

    export default {
        components: {
            "open-vpn-node-form": OpenVPNNodeForm,
            "grpc-node-form": GRPCNodeForm,
            "local-dns-node-form": LocalDNSNodeForm,
            MetadataForm,
            CountryAutocomplete,
            "city-node-form": CityNodeForm,
        },
        data() {
            return {
                // server bundles
                sbIndex: [],
                sbList: [],
                sbValue: "",
                sbs: [],

                errorMsg: null,
                submitted: false,
                processing: false,

                componentKey: 0,
                showNodeDialog: false,
                node: {
                    protocol: "OpenVPN_UDP",
                    ip: "127.0.0.1",
                    server_address: "",
                    port: 0,
                    secret: "",
                    os: "",
                    location: {
                        longitude: "",
                        latitude: "",
                        countryCode: "",
                        city: {
                            en: ""
                        }
                    },
                    capacity: 0,
                    tags: [],
                    is_enabled: true,
                    provider: "ProVPN",
                    payload: "{}",
                    load_index: 0,
                    preshared_secret: "",
                    metadata: {
                        title: {},
                        description: {},
                        icon: ""
                    }
                },
            };
        },
        validations: {
            node: {
                protocol: {required},
                provider: {required},
                location: {
                  countryCode: {required, isValidCountryCode},
                  city: LOCALES_LIST.reduce((acc, locale) => {
                    acc[locale] = ({ required });
                    return acc;
                  }, {})
                }
            }
        },
        mounted() {
            this.getServerBundles();
        },
        methods: {
            getServerBundles() {
                this.$http.get("/v1/admin/node/server-bundles").then(
                    res => {
                        let names = [];

                        res.data["server_bundles"].forEach(el => names.push(el.name));

                        this.sbList = res.data.server_bundles;
                        this.sbIndex = names;
                    },
                    err => {
                        console.error(err);
                    },
                );
            },
            addServer() {
                this.sbs.push(this.sbValue);

                this.sbValue = "";
            },
            deleteChip(item) {
                this.sbs = this.sbs.filter(el => {
                    return el !== item;
                });
            },
            createNode() {
                this.submitted = true;
                this.errorMsg = null;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    console.log(this.$v.$error);
                    return;
                }

                let reqServiceBundles = [];

                this.sbList.forEach(sb => {
                    this.sbs.forEach(selectedSb => {
                        if (selectedSb === sb.name) {
                            reqServiceBundles.push(sb);
                        }
                    });
                });

                let body = filterNodeBody({
                    protocol: this.node.protocol,
                    ip: this.node.ip,
                    server_address: this.node.server_address,
                    port: parseInt(this.node.port, 10),
                    secret: this.node.secret,
                    os: this.node.os,
                    location: {
                        longitude: parseFloat(this.node.location.longitude),
                        latitude: parseFloat(this.node.location.latitude),
                        countryCode: this.node.location.countryCode,
                        city: this.node.location.city
                    },
                    capacity: parseInt(this.node.capacity, 10),
                    server_bundles: reqServiceBundles,
                    is_enabled: this.node.is_enabled,
                    provider: this.node.provider,
                    payload: JSON.parse(this.node.payload.replace(/(\r\n|\n|\r)/gm, "")),
                    comment: this.node.comment,
                    load_index: this.node.load_index,
                    metadata: this.node.metadata,
                    preshared_secret: this.node.preshared_secret
                });

                this.processing = true;
                this.$http.post("/v1/admin/node/localization-upsert", { localization: [ { city: body.location.city } ] })
                  .then(() => this.$http.post("/v1/admin/node/create", body)).then(
                    () => {
                        this.$notify({
                            group: "foo",
                            title: "Node created",
                        });

                        this.processing = false;
                        this.$router.push("/nodes");
                    },
                    err => {
                        this.processing = false;
                        this.errorMsg = err.response.data.message;
                    },
                );
            },
            toggleNodeDialog() {
                this.showNodeDialog = !this.showNodeDialog;
            },
            rerenderTable() {
                this.componentKey += 1;
            },
            updateLocationCountryCode(newValue) {
                this.node.location.countryCode = newValue;

                this.node.metadata.title = {
                  en: countryCodeToName[newValue],
                  uk: countryCodeToUkName[newValue],
                };
            },
        }
    };
</script>


<style lang="css" scoped>
    .shake-on-error .md-duplicated {
        animation-name: shake;
        animation-duration: 0.5s;
    }

    @keyframes shake {
        0% {
            transform: translate(15px);
        }
        20% {
            transform: translate(-15px);
        }
        40% {
            transform: translate(7px);
        }
        60% {
            transform: translate(-7px);
        }
        80% {
            transform: translate(3px);
        }
        100% {
            transform: translate(0px);
        }
    }

    .pulse-on-error >>> .md-duplicated {
        animation-name: pulse;
        animation-duration: 0.5s;
        animation-iteration-count: infinite;
        animation-direction: alternate;
        animation-timing-function: ease-in-out
    }

    @keyframes pulse {
        0% {
            transform: scale(1.1, 1.1);
        }
        100% {
            transform: scale(0.9, 0.9);
        }
    }

    .md-layout {
        padding: 0 50px;
    }

    .node-form {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        max-width: 1400px;
    }

    .node-form__left {
        display: flex;
        flex-direction: column;
        width: 80%;
    }

    .node-form__general {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }

    .node-form__general--left {
        width: 65%;
    }

    .node-form__general--right {
        width: 30%;
    }

    .tags__input-field {
        width: 80%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }

    .tags__input {
        width: 90%;
    }

    .tags_selected {
        margin-bottom: 15px;
    }

    .gen-key {
        cursor: pointer !important;
    }

    .gen-key:active {
        position: relative;
        top: 2px;
    }

    .md-field > .md-icon:after {
        display: none !important;
    }

    .location {
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        column-gap: 24px;
    }

    .form-submit {
        width: 150px;
        height: 60px;
    }

    .error-feedback {
        font-size: 12px;
        color: red;
    }
</style>
